import { BACKUP_FORMAT_VERSION } from '~/models/schemaVersion'

/**
 * Full recovery backup container (D-030).
 *
 *   sola-backup-YYYYMMDD.zip
 *   ├─ manifest.json
 *   ├─ data.json
 *   └─ blobs/<blobId>
 *
 * A backup ALWAYS contains all app data, photos, and attachments (D-018).
 * There are no privacy toggles — that surface belongs to AI-review exports.
 * Large libraries are handled by segmentation, never by dropping media.
 */

export const BACKUP_FORMAT = 'project-sola-backup'
export const MANIFEST_FILE = 'manifest.json'
export const DATA_FILE = 'data.json'
export const BLOB_DIRECTORY = 'blobs'

export interface BlobManifestEntry {
  readonly blobId: string
  readonly sha256: string
  readonly byteSize: number
  readonly mimeType: string
  /** Path inside the container, e.g. `blobs/<blobId>`. */
  readonly path: string
  /** Which part holds it, for a segmented set. Single-file backups use 1. */
  readonly segment: number
}

export interface SegmentEntry {
  readonly index: number
  readonly of: number
  readonly fileName: string
}

export interface BackupManifest {
  readonly format: typeof BACKUP_FORMAT
  readonly backupFormatVersion: number
  readonly dbSchemaVersion: number
  readonly appVersion: string
  readonly createdAt: string
  /** Record counts per store, checked again after a restore. */
  readonly recordCounts: Readonly<Record<string, number>>
  readonly blobCount: number
  readonly blobTotalBytes: number
  /** SHA-256 of `data.json`, so tampering is detectable before any mutation. */
  readonly dataSha256: string
  readonly blobs: readonly BlobManifestEntry[]
  readonly units: { readonly weight: 'kg'; readonly length: 'cm' }
  readonly locale: string
  /** Present only when the backup spans several files. */
  readonly segments?: readonly SegmentEntry[]
  /**
   * Explicit completeness declaration (D-030). A restore refuses anything that
   * does not claim to be complete, so a partial artifact can never masquerade
   * as a recovery backup.
   */
  readonly containsAllData: true
}

/** Structured records, keyed by store name. Blob bytes live in the container. */
export interface BackupPayload {
  readonly dbSchemaVersion: number
  readonly stores: Readonly<Record<string, readonly unknown[]>>
}

export const CURRENT_BACKUP_FORMAT_VERSION = BACKUP_FORMAT_VERSION

export function backupFileName(createdAt: Date, segment?: SegmentEntry): string {
  const stamp = createdAt.toISOString().slice(0, 10).replace(/-/g, '')
  return segment === undefined
    ? `sola-backup-${stamp}.zip`
    : `sola-backup-${stamp}-part${segment.index}of${segment.of}.zip`
}

export function blobPath(blobId: string): string {
  return `${BLOB_DIRECTORY}/${blobId}`
}
